import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  RefreshCw,
  ScanSearch,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { CropValidationModel } from "./cropValidationModel";

export function CropValidationPanel({
  validation,
  onInspectRisk,
  onShowRisk,
}: {
  validation: CropValidationModel;
  onInspectRisk: () => void;
  onShowRisk: () => void;
}) {
  const statusCopy = {
    ready: {
      label: "Evidence complete",
      description:
        "All scenes have a plan, applied receipt, and encoded-delivery QC with no recorded crop issue.",
    },
    attention: {
      label: "Review required",
      description:
        "At least one scene has failed QC, a plan/application mismatch, or crop feedback.",
    },
    incomplete: {
      label: "Evidence incomplete",
      description:
        "This Echo cannot yet prove the full plan → render → encoded-delivery chain.",
    },
  }[validation.status];

  const scrollToRemakes = () => {
    document
      .getElementById("remakes-card")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border p-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {validation.status === "ready" ? (
              <CheckCircle2 className="size-4 text-emerald-600" />
            ) : validation.status === "attention" ? (
              <AlertTriangle className="size-4 text-amber-600" />
            ) : (
              <CircleDashed className="size-4 text-muted-foreground" />
            )}
            <strong>{statusCopy.label}</strong>
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            {statusCopy.description}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onShowRisk}
          >
            <ScanSearch className="size-4" />
            Show risky scenes
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onInspectRisk}
            disabled={validation.firstRiskSceneId == null}
          >
            Inspect first risk
          </Button>
          <Button type="button" size="sm" onClick={scrollToRemakes}>
            <RefreshCw className="size-4" />
            Re-render current Echo
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {validation.checks.map((check) => {
          const done = check.total > 0 && check.complete === check.total;
          return (
            <div key={check.id} className="rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{check.label}</span>
                <Badge variant={done ? "secondary" : "outline"}>
                  {check.complete}/{check.total}
                </Badge>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {done
                  ? "Complete for every scene."
                  : "Missing evidence must remain visible; it is never inferred."}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-md bg-muted p-3">
          <div className="text-xl font-semibold">{validation.failedQc}</div>
          <div className="text-muted-foreground">failed QC</div>
        </div>
        <div className="rounded-md bg-muted p-3">
          <div className="text-xl font-semibold">
            {validation.planAppliedMismatches}
          </div>
          <div className="text-muted-foreground">plan mismatches</div>
        </div>
        <div className="rounded-md bg-muted p-3">
          <div className="text-xl font-semibold">
            {validation.fallbackOrRepair}
          </div>
          <div className="text-muted-foreground">fallback / repair</div>
        </div>
        <div className="rounded-md bg-muted p-3">
          <div className="text-xl font-semibold">{validation.cropFeedback}</div>
          <div className="text-muted-foreground">crop feedback</div>
        </div>
        <div className="rounded-md bg-muted p-3">
          <div className="text-xl font-semibold">
            {validation.uncorrelatedFeedback}
          </div>
          <div className="text-muted-foreground">uncorrelated reports</div>
        </div>
      </div>

      <ol className="grid gap-3 text-sm md:grid-cols-2">
        <li className="rounded-lg border p-3">
          <strong>1. Freeze a baseline.</strong>
          <p className="mt-1 text-muted-foreground">
            Inspect affected scenes and record the attempt, plan digest, crop
            geometry, and encoded frame.
          </p>
        </li>
        <li className="rounded-lg border p-3">
          <strong>2. Re-render through the current pipeline.</strong>
          <p className="mt-1 text-muted-foreground">
            Use Remakes. A preview is isolated until an administrator explicitly
            approves delivery.
          </p>
        </li>
        <li className="rounded-lg border p-3">
          <strong>3. Compare attempts.</strong>
          <p className="mt-1 text-muted-foreground">
            Open Crop Forensics and select attempt B to compare the same scene
            and stage with linked pan and zoom.
          </p>
        </li>
        <li className="rounded-lg border p-3">
          <strong>4. Validate delivered pixels.</strong>
          <p className="mt-1 text-muted-foreground">
            Require encoded-delivery QC and subject overlays; metadata-only
            success is insufficient.
          </p>
        </li>
      </ol>
    </div>
  );
}
